import { formatWhen } from "@/components/boo/access-copy";
import { InlineError } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api, type ConnectorSetupPolicy } from "@/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

const SETUP_CHOICES: { value: ConnectorSetupPolicy; label: string; hint: string }[] = [
  {
    value: "everyone",
    label: "Anyone Boo sends a link to",
    hint: "They fill it in without signing in and see only the connection form. Everyone in the organization can also fill it in from the dashboard.",
  },
  {
    value: "admins",
    label: "Only Boo admins",
    hint: "Only the owners and admins of that Boo can fill it in. They sign in to do it.",
  },
];

// Who may fill in the connection a custom integration Boo built asks for: anyone
// Boo sends the link to, or only that Boo's admins. Org-wide, on Integrations.
export function ConnectorSetupSection({ organizationId }: { organizationId: string }) {
  const queryClient = useQueryClient();
  const key = ["org-settings", organizationId];
  const query = useQuery({ queryKey: key, queryFn: () => api.org.settings() });
  const [showHistory, setShowHistory] = useState(false);
  const update = useMutation({
    mutationFn: (connectorSetup: ConnectorSetupPolicy) => api.org.updateSettings({ connectorSetup }),
    onSuccess: (next) => {
      queryClient.setQueryData(key, next);
      toast.success(
        next.connectorSetup === "everyone" ? "Anyone Boo sends a link to can set up custom integrations" : "Only Boo admins can set up custom integrations",
      );
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not save"),
  });
  const settings = query.data;
  const canEdit = settings?._permissions.canEdit === true;
  const value = update.isPending ? update.variables : settings?.connectorSetup;

  return (
    <section className="max-w-xl space-y-3 border-t pt-8">
      <div>
        <h2 className="text-base font-semibold">Who can set up custom integrations</h2>
        <p className="text-sm text-muted-foreground text-pretty">
          When Boo builds a custom integration for a tool, it asks whoever holds the key to fill in the connection.
          This decides who may do that.
        </p>
      </div>
      {query.isLoading ? (
        <div className="h-24" />
      ) : query.isError || !settings ? (
        <InlineError error={query.error ?? "Failed to load settings"} />
      ) : (
        <>
          <RadioGroup
            value={value}
            onValueChange={(v) => update.mutate(v as ConnectorSetupPolicy)}
            disabled={!canEdit || update.isPending}
            className="gap-3"
            aria-label="Who can set up custom integrations"
          >
            {SETUP_CHOICES.map((c) => (
              <div key={c.value} className="flex items-start gap-2.5 rounded-lg border p-3">
                <RadioGroupItem value={c.value} id={`connector-setup-${c.value}`} className="mt-0.5" />
                <Label htmlFor={`connector-setup-${c.value}`} className="flex flex-col items-start gap-0.5 font-normal leading-snug">
                  <span className="text-sm font-medium">
                    {c.label}
                    {c.value === "everyone" && <span className="ml-1.5 text-xs font-normal text-muted-foreground">Default</span>}
                  </span>
                  <span className="text-xs text-muted-foreground text-pretty">{c.hint}</span>
                </Label>
              </div>
            ))}
          </RadioGroup>
          {!canEdit && <p className="text-xs text-muted-foreground">Only owners and admins can change this.</p>}
          {settings.updatedBy && settings.updatedAt && (
            <p className="text-xs text-muted-foreground">
              Changed by {settings.updatedBy.name}, {formatWhen(settings.updatedAt)}.
              {settings.history.length > 1 && (
                <Button variant="link" size="sm" className="ml-1 h-auto p-0 text-xs" onClick={() => setShowHistory((s) => !s)}>
                  {showHistory ? "Hide earlier changes" : "Show earlier changes"}
                </Button>
              )}
            </p>
          )}
          {showHistory && settings.history.length > 0 && (
            <ul className="divide-y rounded-lg border text-xs">
              {settings.history.map((h, i) => (
                <li key={`${h.at}-${i}`} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-3 py-2">
                  <span className="font-medium">{h.actor.name}</span>
                  <span className="text-muted-foreground">
                    {setupLabel(h.from)} → {setupLabel(h.to)}
                  </span>
                  <span className="ml-auto text-muted-foreground">{formatWhen(h.at)}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

const setupLabel = (v: ConnectorSetupPolicy) => (v === "everyone" ? "Anyone Boo sends a link to" : "Only Boo admins");
